import React, {
  createContext, useCallback, useState, useContext,
} from 'react';

interface User {
  email: string;
}

interface AuthState {
  token: string;
  user: User;
}

interface SignInCredentials {
  email: string;
  password: string;
}

interface AuthContextData {
  user: User;
  signIn(credentials: SignInCredentials): Promise<void>;
  signOut(): void;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export const AuthProvider: React.FC = ({ children }) => {
  const [data, setData] = useState<AuthState>(() => {
    const token = localStorage.getItem('@Investify:token');
    const user = localStorage.getItem('@Investify:user');

    if (token && user) {
      return { token, user: JSON.parse(user) };
    }

    return {} as AuthState;
  });

  const signIn = useCallback(async ({ email, password }) => fetch(`${process.env.REACT_APP_API_URL}/auth/login`, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    method: 'POST',
    body: JSON.stringify({
      email,
      password,
    }),
  })
    .then((response) => response.json())
    .then((response) => {
      const { token, user, error_description } = response;

      if (error_description) throw error_description;

      localStorage.setItem('@Investify:token', token);
      localStorage.setItem('@Investify:user', JSON.stringify(user));

      setData({ token, user });
    }), []);

  const signOut = useCallback(() => {
    localStorage.removeItem('@Investify:token');
    localStorage.removeItem('@Investify:user');

    setData({} as AuthState);
  }, []);

  return (
    <AuthContext.Provider value={{
      user: data.user, signIn, signOut,
    }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextData {
  return useContext(AuthContext);
}
